import React, { useState } from 'react';
import { Box, Checkbox, Chip, Divider, FormControlLabel, Grid, IconButton, Typography } from '@mui/material';
import { Delete, DragIndicator } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AccountData, CalendarDisplayItem } from '../api';
import { AccountService } from '../api/services/AccountService';
import { accountHasEventOrderPages, accountHasPrivateOrderPages } from '../utils/AccountUtils';

interface CalendarDisplaySettingsProps {
  account: AccountData;
  updateLocalAccount: (account: AccountData) => void;
  setIsUpdatedSnackOpen: (open: boolean) => void;
}

interface SortableItemProps {
  id: string;
  item: CalendarDisplayItem;
  onRemove: (item: CalendarDisplayItem) => void;
  getItemLabel: (item: CalendarDisplayItem) => string;
  disabled?: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, item, onRemove, getItemLabel, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Box
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '6px',
          bgcolor: disabled ? 'mb.disabledFill' : 'background.paper',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          opacity: disabled ? 0.6 : 1,
          '&:hover': !disabled
            ? {
                bgcolor: 'mb.ground',
                borderColor: 'mb.control',
              }
            : {},
        }}>
        <Box
          {...(disabled ? {} : listeners)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: disabled ? 'not-allowed' : 'grab',
            flexGrow: 1,
            '&:active': {
              cursor: disabled ? 'not-allowed' : 'grabbing',
            },
          }}>
          <DragIndicator sx={{ marginInlineEnd: 1, color: disabled ? 'text.disabled' : 'text.secondary' }} />
          <Typography color={disabled ? 'text.disabled' : 'inherit'}>{getItemLabel(item)}</Typography>
        </Box>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={() => onRemove(item)}
          sx={{
            color: 'error.main',
          }}>
          <Delete fontSize="small" />
        </IconButton>
      </Box>
    </div>
  );
};

interface DisplayConfigProps {
  title: string;
  availableItems: CalendarDisplayItem[];
  currentPriorities: CalendarDisplayItem[];
  onUpdate: (priorities: CalendarDisplayItem[]) => void;
  disabled?: boolean;
}

const DisplayConfig: React.FC<DisplayConfigProps> = ({
  title,
  availableItems,
  currentPriorities,
  onUpdate,
  disabled,
}) => {
  const { t } = useTranslation();
  const [localPriorities, setLocalPriorities] = useState(currentPriorities);
  const [availableForSelection, setAvailableForSelection] = useState(
    availableItems.filter((item) => !currentPriorities.includes(item)),
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localPriorities.indexOf(active.id as CalendarDisplayItem);
      const newIndex = localPriorities.indexOf(over.id as CalendarDisplayItem);
      const newPriorities = arrayMove(localPriorities, oldIndex, newIndex);

      setLocalPriorities(newPriorities);
      onUpdate(newPriorities);
    }
  };

  const handleRemoveItem = (item: CalendarDisplayItem) => {
    const newPriorities = localPriorities.filter((i) => i !== item);
    const newAvailable = [...availableForSelection, item];

    setLocalPriorities(newPriorities);
    setAvailableForSelection(newAvailable);
    onUpdate(newPriorities);
  };

  const handleAddItem = (item: CalendarDisplayItem) => {
    const newPriorities = [...localPriorities, item];
    const newAvailable = availableForSelection.filter((i) => i !== item);

    setLocalPriorities(newPriorities);
    setAvailableForSelection(newAvailable);
    onUpdate(newPriorities);
  };

  const getItemLabel = (item: CalendarDisplayItem) => {
    switch (item) {
      case CalendarDisplayItem.EMPLOYEES:
        return t('accountSettings.calendarDisplay.items.employees');
      case CalendarDisplayItem.CUSTOMER_NAME:
        return t('accountSettings.calendarDisplay.items.customerName');
      case CalendarDisplayItem.ORDER_NUMBER:
        return t('accountSettings.calendarDisplay.items.orderNumber');
      case CalendarDisplayItem.CHECKIN_TIME:
        return t('accountSettings.calendarDisplay.items.checkinTime');
      case CalendarDisplayItem.EVENT_NAME:
        return t('accountSettings.calendarDisplay.items.eventName');
      case CalendarDisplayItem.LOCATION:
        return t('accountSettings.calendarDisplay.items.location');
      case CalendarDisplayItem.CUSTOM_DETAILS:
        return t('accountSettings.calendarDisplay.items.customDetails');
      default:
        return item;
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('accountSettings.calendarDisplay.activeItems')}
      </Typography>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={localPriorities} strategy={verticalListSortingStrategy}>
          <Grid container flexDirection="column" gap={1} sx={{ mb: 2, width: '100%', maxWidth: 360 }}>
            {localPriorities.map((item) => (
              <SortableItem
                key={item}
                id={item}
                item={item}
                onRemove={handleRemoveItem}
                getItemLabel={getItemLabel}
                disabled={disabled}
              />
            ))}
          </Grid>
        </SortableContext>
      </DndContext>

      {availableForSelection.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('accountSettings.calendarDisplay.availableItems')}
          </Typography>
          <Grid container gap={1}>
            {availableForSelection.map((item) => (
              <Chip
                key={item}
                label={getItemLabel(item)}
                onClick={() => handleAddItem(item)}
                variant="outlined"
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export const CalendarDisplaySettings: React.FC<CalendarDisplaySettingsProps> = ({
  account,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
}) => {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [summarizeBusyDays, setSummarizeBusyDays] = useState(!!account?.calendar_display_settings?.summarize_busy_days);

  const hasEventPages = accountHasEventOrderPages(account);
  const hasPrivatePages = accountHasPrivateOrderPages(account);
  const orderOnlyItems = [
    CalendarDisplayItem.CUSTOMER_NAME,
    CalendarDisplayItem.ORDER_NUMBER,
    CalendarDisplayItem.CHECKIN_TIME,
  ];
  const eventOnlyItems = [
    CalendarDisplayItem.EVENT_NAME,
    CalendarDisplayItem.LOCATION,
    CalendarDisplayItem.CUSTOM_DETAILS,
  ];
  const sharedItems = [CalendarDisplayItem.EMPLOYEES];

  const orderItems = [...sharedItems, ...orderOnlyItems];
  const eventItems = [...sharedItems, ...eventOnlyItems];

  const currentOrderPriorities = account?.calendar_display_settings?.order_display_priorities || [
    CalendarDisplayItem.EMPLOYEES,
    CalendarDisplayItem.CUSTOMER_NAME,
    CalendarDisplayItem.ORDER_NUMBER,
  ];

  const currentEventPriorities = account?.calendar_display_settings?.event_display_priorities || [
    CalendarDisplayItem.EMPLOYEES,
    CalendarDisplayItem.EVENT_NAME,
    CalendarDisplayItem.LOCATION,
  ];

  const handleUpdateSettings = async (updates: {
    order_display_priorities?: CalendarDisplayItem[];
    event_display_priorities?: CalendarDisplayItem[];
    summarize_busy_days?: boolean;
  }) => {
    setIsUpdating(true);
    try {
      await AccountService.updateCalendarDisplaySettings(updates);
      updateLocalAccount(account);
      setIsUpdatedSnackOpen(true);
    } catch (error) {
      console.error('Failed to update calendar display settings:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Grid container flexDirection="column">
      <Typography variant="h5" sx={{ mb: 3 }}>
        {t('accountSettings.calendarDisplay.title')}
      </Typography>

      <FormControlLabel
        sx={{ mb: 3 }}
        control={
          <Checkbox
            checked={summarizeBusyDays}
            disabled={isUpdating}
            onChange={(event) => {
              setSummarizeBusyDays(event.target.checked);
              void handleUpdateSettings({ summarize_busy_days: event.target.checked });
            }}
          />
        }
        label={t('accountSettings.calendarDisplay.summarizeBusyDays')}
      />

      {hasPrivatePages && (
        <>
          <DisplayConfig
            title={t('accountSettings.calendarDisplay.orderTitle')}
            availableItems={orderItems}
            currentPriorities={currentOrderPriorities}
            onUpdate={(priorities) => handleUpdateSettings({ order_display_priorities: priorities })}
            disabled={isUpdating}
          />
          {hasEventPages && <Divider sx={{ my: 3 }} />}
        </>
      )}

      {hasEventPages && (
        <DisplayConfig
          title={t('accountSettings.calendarDisplay.eventTitle')}
          availableItems={eventItems}
          currentPriorities={currentEventPriorities}
          onUpdate={(priorities) => handleUpdateSettings({ event_display_priorities: priorities })}
          disabled={isUpdating}
        />
      )}
    </Grid>
  );
};
